import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Check, X, ChevronDown, Loader2, Sparkles } from "lucide-react";
import { INFLUENCER_PLAN, DAILY_RITUAL, formatDayLabel } from "@/lib/influencerPlan";

export const Route = createFileRoute("/_authenticated/admin/influencer")({
  head: () => ({ meta: [{ title: "Influencer DM — Admin" }] }),
  component: InfluencerPage,
});

type Status = "pendiente" | "realizado" | "no_realizado";
type Row = { action_date: string; status: Status };

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function InfluencerPage() {
  const qc = useQueryClient();
  const today = todayIso();
  const [open, setOpen] = useState<string | null>(
    INFLUENCER_PLAN.some((d) => d.date === today) ? today : INFLUENCER_PLAN[0]?.date ?? null,
  );
  const [filter, setFilter] = useState<"todos" | "pendiente" | "realizado" | "no_realizado">("todos");

  const { data, isLoading } = useQuery({
    queryKey: ["influencer-actions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("influencer_actions")
        .select("action_date, status");
      if (error) throw error;
      return (data ?? []) as Row[];
    },
  });

  const statusByDate = useMemo(() => {
    const map: Record<string, Status> = {};
    for (const r of data ?? []) map[r.action_date] = r.status;
    return map;
  }, [data]);

  const done = INFLUENCER_PLAN.filter((d) => statusByDate[d.date] === "realizado").length;
  const notDone = INFLUENCER_PLAN.filter((d) => statusByDate[d.date] === "no_realizado").length;
  const pending = INFLUENCER_PLAN.length - done - notDone;
  const pct = Math.round((done / INFLUENCER_PLAN.length) * 100);

  const setStatus = async (date: string, status: Status) => {
    const current = statusByDate[date] ?? "pendiente";
    const next: Status = current === status ? "pendiente" : status;
    const prev = qc.getQueryData<Row[]>(["influencer-actions"]);
    qc.setQueryData<Row[]>(["influencer-actions"], (old) => {
      const list = (old ?? []).filter((r) => r.action_date !== date);
      return [...list, { action_date: date, status: next }];
    });
    const { error } = await supabase
      .from("influencer_actions")
      .upsert({ action_date: date, status: next }, { onConflict: "action_date" });
    if (error) {
      qc.setQueryData(["influencer-actions"], prev);
      toast.error(error.message);
    }
  };

  const list = INFLUENCER_PLAN.filter((d) =>
    filter === "todos" ? true : (statusByDate[d.date] ?? "pendiente") === filter,
  );

  return (
    <div className="mx-auto w-full max-w-2xl space-y-5 pb-24">
      <header>
        <h1 className="font-serif text-2xl font-bold">Influencer DM</h1>
        <p className="text-sm text-muted-foreground">Calendario operativo de contenidos — Septiembre 2026</p>
      </header>

      {/* Resumen del mes */}
      <section className="rounded-xl border border-border bg-card p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold">Resumen del mes</h2>
          <span className="text-xs text-muted-foreground">{pct}% completado</span>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="rounded-lg bg-emerald-500/10 p-3">
            <div className="text-2xl font-bold text-emerald-600">{done}</div>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Realizados</div>
          </div>
          <div className="rounded-lg bg-red-500/10 p-3">
            <div className="text-2xl font-bold text-red-600">{notDone}</div>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">No realizados</div>
          </div>
          <div className="rounded-lg bg-secondary p-3">
            <div className="text-2xl font-bold">{pending}</div>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Pendientes</div>
          </div>
        </div>
        <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-secondary">
          <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
        </div>
      </section>

      {/* Ritual diario */}
      <section className="rounded-xl border border-accent/40 bg-accent/10 p-4">
        <div className="mb-1 flex items-center gap-2 text-sm font-semibold">
          <Sparkles className="h-4 w-4" /> Ritual diario
        </div>
        <p className="text-xs leading-relaxed text-muted-foreground">{DAILY_RITUAL}</p>
      </section>

      {/* Filtros */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {(["todos", "pendiente", "realizado", "no_realizado"] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition ${
              filter === f ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"
            }`}
          >
            {f === "no_realizado" ? "No realizados" : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="space-y-3">
          {list.map((day) => {
            const status = statusByDate[day.date] ?? "pendiente";
            const expanded = open === day.date;
            const isToday = day.date === today;
            return (
              <article
                key={day.date}
                className={`overflow-hidden rounded-xl border bg-card ${
                  isToday ? "border-accent ring-1 ring-accent/40" : "border-border"
                }`}
              >
                <button
                  onClick={() => setOpen(expanded ? null : day.date)}
                  className="flex w-full items-start gap-3 p-4 text-left"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        {formatDayLabel(day.date)}
                      </span>
                      {isToday && (
                        <span className="rounded-full bg-accent px-2 py-0.5 text-[10px] font-bold text-accent-foreground">
                          HOY
                        </span>
                      )}
                      {status === "realizado" && (
                        <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-bold text-emerald-600">
                          REALIZADO
                        </span>
                      )}
                      {status === "no_realizado" && (
                        <span className="rounded-full bg-red-500/15 px-2 py-0.5 text-[10px] font-bold text-red-600">
                          NO REALIZADO
                        </span>
                      )}
                    </div>
                    <h3 className="mt-1 font-serif text-base font-semibold leading-snug">{day.title}</h3>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {day.tipo} · {day.formato}
                    </p>
                  </div>
                  <ChevronDown
                    className={`mt-1 h-4 w-4 shrink-0 text-muted-foreground transition ${expanded ? "rotate-180" : ""}`}
                  />
                </button>

                {expanded && (
                  <div className="space-y-3 border-t border-border px-4 pb-4 pt-3 text-sm">
                    <Field label="Objetivo" value={day.objetivo} />
                    <Field label="Contenido a generar" value={day.contenido} />
                    <Field label="Guion listo para grabar" value={day.guion} quote />
                    <Field label="Tomas sugeridas" value={day.tomas} />
                    <Field label="CTA" value={day.cta} />
                    <Field label="Checklist antes de publicar" value={day.checklist} />
                    <Field label="Éxito del día" value={day.exito} />
                  </div>
                )}

                <div className="grid grid-cols-2 gap-2 border-t border-border p-3">
                  <button
                    onClick={() => setStatus(day.date, "realizado")}
                    className={`flex items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold transition ${
                      status === "realizado"
                        ? "bg-emerald-600 text-white"
                        : "bg-secondary text-foreground hover:bg-emerald-500/10"
                    }`}
                  >
                    <Check className="h-4 w-4" /> Realizado
                  </button>
                  <button
                    onClick={() => setStatus(day.date, "no_realizado")}
                    className={`flex items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold transition ${
                      status === "no_realizado"
                        ? "bg-red-600 text-white"
                        : "bg-secondary text-foreground hover:bg-red-500/10"
                    }`}
                  >
                    <X className="h-4 w-4" /> No realizado
                  </button>
                </div>
              </article>
            );
          })}
          {list.length === 0 && (
            <p className="py-8 text-center text-sm text-muted-foreground">Sin contenidos en este filtro.</p>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, value, quote }: { label: string; value: string; quote?: boolean }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
      <p
        className={`mt-0.5 whitespace-pre-line leading-relaxed ${
          quote ? "rounded-lg bg-secondary p-3 text-[13px] italic" : "text-[13px]"
        }`}
      >
        {value}
      </p>
    </div>
  );
}
